import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Crown, Check, FileText, ShieldCheck, Video, Sparkles, CalendarClock } from "lucide-react";
import { GFFrame } from "@/components/GFFrame";
import { clarityProduct, claritySign, clarityPay, clarityStatus, clarityAgreementUrl } from "@/lib/gfApi";
import { storeAiEmail } from "@/pages/ai/aiShared";

const inputCls = "w-full rounded-2xl border border-[#E7DED2] bg-white px-4 py-3 text-[15px] outline-none focus:border-[#B8860B]";

export default function BusinessClarity() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("order") || "";

  const [product, setProduct] = useState(null);
  const [step, setStep] = useState("review"); // review | agreement | pay | active
  const [form, setForm] = useState({ name: "", business: "", email: "", signature: "", accepted: false });
  const [agreementId, setAgreementId] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => { clarityProduct().then(setProduct).catch(() => {}); }, []);

  // Returning from ToyyibPay: verify entitlement.
  useEffect(() => {
    if (!orderId) return;
    setStep("pay");
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const s = await clarityStatus({ order_id: orderId });
        setStatus(s);
        if (s.entitled) { clearInterval(iv); setStep("active"); }
      } catch { /* keep polling */ }
      if (tries > 12) clearInterval(iv);
    }, 2500);
    return () => clearInterval(iv);
  }, [orderId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const sign = async () => {
    if (!form.name.trim() || !form.signature.trim() || !form.email.trim()) { toast.error("Sila lengkapkan nama, emel & tandatangan."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { toast.error("Emel tidak sah."); return; }
    if (!form.accepted) { toast.error("Sila tanda kotak persetujuan."); return; }
    setBusy(true);
    try {
      const res = await claritySign({ ...form, email: form.email.trim().toLowerCase() });
      setAgreementId(res.agreement_id);
      storeAiEmail(form.email.trim().toLowerCase());
      toast.success("Perjanjian ditandatangani & dihantar ke emel anda.");
      setStep("pay");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan perjanjian."); }
    finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const res = await clarityPay({ agreement_id: agreementId, phone: "" });
      if (res.payment_url) window.location.assign(res.payment_url);
      else throw new Error("no url");
    } catch (e) { setBusy(false); toast.error(e?.response?.data?.detail || "Gagal memulakan pembayaran."); }
  };

  const Included = ({ children }) => (
    <li className="flex items-start gap-2 text-sm text-[#E7DED2]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A441]" /> {children}</li>
  );

  return (
    <GFFrame testId="business-clarity-page">
      <div className="min-h-screen bg-[#F4F1EA] pb-16">
        <div className="bg-[#3B2A20] px-6 pb-8 pt-9 text-white">
          <button onClick={() => navigate("/")} data-testid="clarity-back-btn" className="flex items-center gap-1 text-sm text-[#CBB79E]">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
          <div className="mt-4 flex items-center gap-2">
            <Crown className="h-5 w-5 text-[#D9A441]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D9A441]">Business Clarity Session</span>
          </div>
          <h1 className="mt-2 font-heading text-3xl font-bold leading-tight">Business Clarity Session with Dr. Irma</h1>
          <p className="mt-2 text-sm leading-relaxed text-[#CBB79E]">One business. One conversation. A clearer way forward. Sesi mentoring bisnes online 60 minit.</p>
          <ul className="mt-4 space-y-1.5">
            <Included>Sesi 1-to-1 60 minit bersama Dr. Irma (online)</Included>
            <Included>Akses penuh Business Growth</Included>
            <Included>50 kredit AI Campaign Lab (sah 3 bulan)</Included>
          </ul>
          <div className="mt-5 flex items-baseline gap-2">
            <span className="font-heading text-4xl font-bold text-[#D9A441]">RM500</span>
            <span className="text-xs text-[#CBB79E]">sekali bayar</span>
          </div>
        </div>

        <div className="px-6 pt-6">
          {step === "review" && (
            <div data-testid="clarity-review">
              <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[[Video, "60 min online"], [Sparkles, "50 kredit AI"], [CalendarClock, "Tempah sesi"]].map(([I, t], i) => (
                    <div key={i} className="rounded-xl bg-[#F7F2EA] py-3"><I className="mx-auto h-5 w-5 text-[#8B5E3C]" /><p className="mt-1 text-[11px] font-semibold text-[#3B2A20]">{t}</p></div>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">Sebelum pembayaran, anda perlu membaca & menandatangani Terms of Engagement secara elektronik. Salinan akan diemelkan kepada anda.</p>
              </div>
              <button data-testid="clarity-review-sign-btn" onClick={() => setStep("agreement")} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#3B2A20] py-3.5 text-sm font-bold text-white">
                <FileText className="h-4 w-4" /> Review & Sign Agreement
              </button>
            </div>
          )}

          {step === "agreement" && product && (
            <div data-testid="clarity-agreement">
              <h2 className="font-heading text-xl font-bold text-[#3B2A20]">Terms of Engagement</h2>
              <p className="mt-1 text-xs text-slate-400">Versi {product.agreement_version}</p>
              <div data-testid="agreement-scrollbox" className="mt-3 max-h-64 overflow-y-auto rounded-2xl border border-[#E7DED2] bg-white p-4 text-[13px] leading-relaxed text-slate-600 whitespace-pre-wrap">
                {product.agreement_text}
              </div>
              <div className="mt-4 space-y-3">
                <input data-testid="clarity-name" className={inputCls} placeholder="Nama penuh (legal) *" value={form.name} onChange={set("name")} />
                <input data-testid="clarity-business" className={inputCls} placeholder="Nama bisnes / syarikat (jika ada)" value={form.business} onChange={set("business")} />
                <input data-testid="clarity-email" type="email" className={inputCls} placeholder="Emel *" value={form.email} onChange={set("email")} />
                <input data-testid="clarity-signature" className={inputCls} placeholder="Tandatangan elektronik (taip nama legal anda) *" value={form.signature} onChange={set("signature")} />
                <label className="flex items-start gap-3 rounded-2xl border border-[#E7DED2] bg-white p-3 text-sm text-slate-600">
                  <input data-testid="clarity-accept" type="checkbox" checked={form.accepted} onChange={set("accepted")} className="mt-0.5 h-5 w-5 accent-[#B8860B]" />
                  <span>Saya telah membaca, memahami dan bersetuju dengan Business Clarity Session Terms of Engagement.</span>
                </label>
              </div>
              <button data-testid="clarity-sign-btn" onClick={sign} disabled={busy} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#B8860B] py-3.5 text-sm font-bold text-white disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Tandatangan & Simpan</>}
              </button>
            </div>
          )}

          {step === "pay" && (
            <div data-testid="clarity-pay" className="text-center">
              {orderId && !status?.entitled ? (
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-[#E7DED2] bg-white p-8">
                  <Loader2 className="h-7 w-7 animate-spin text-[#B8860B]" />
                  <p className="text-sm font-semibold text-[#3B2A20]">Mengesahkan pembayaran anda…</p>
                </div>
              ) : (
                <>
                  <div className="rounded-2xl border border-[#E7DED2] bg-white p-5 text-left">
                    <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><Check className="h-4 w-4" /> Perjanjian ditandatangani</p>
                    {agreementId && <a data-testid="clarity-view-agreement" href={clarityAgreementUrl(agreementId)} target="_blank" rel="noreferrer" className="mt-1 block text-xs text-[#8B5E3C] underline">Lihat / muat turun perjanjian (PDF)</a>}
                    <p className="mt-4 text-sm text-slate-600">Teruskan pembayaran RM500 untuk mengaktifkan pakej anda (50 kredit sah 3 bulan + akses Business Growth + sesi mentoring).</p>
                  </div>
                  <button data-testid="clarity-pay-btn" onClick={pay} disabled={busy || !agreementId} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#B8860B] py-4 text-base font-bold text-white disabled:opacity-60">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Pay RM500 <ArrowRight className="h-5 w-5" /></>}
                  </button>
                </>
              )}
            </div>
          )}

          {step === "active" && (
            <div data-testid="clarity-active" className="text-center">
              <div className="rounded-3xl bg-gradient-to-br from-[#3B2A20] to-[#5A4230] p-7 text-white">
                <Crown className="mx-auto h-10 w-10 text-[#D9A441]" />
                <h2 className="mt-3 font-heading text-2xl font-bold">Your Business Clarity Package is Active</h2>
                <ul className="mt-5 space-y-2 text-left text-sm">
                  <Included>Business Growth unlocked</Included>
                  <Included>AI Campaign Lab unlocked</Included>
                  <Included>50 kredit ditambah, sah 3 bulan (baki: {status?.credits ?? 50})</Included>
                  <Included>Sesi mentoring 60 minit sedia untuk ditempah</Included>
                </ul>
              </div>
              <button data-testid="clarity-start-bg-btn" onClick={() => navigate("/mula")} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#3B2A20] py-3.5 text-sm font-bold text-white">
                Mulakan Business Growth <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-3 text-xs text-slate-400">Tempahan sesi 60 minit akan dibuka sebentar lagi (Fasa 2).</p>
            </div>
          )}
        </div>
      </div>
    </GFFrame>
  );
}
